import { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import SearchBar from "../../components/SearchBar";
import { useNavigate } from "react-router-dom";
import { useLazyGetFeedbacksQuery } from "../../services/feedback";
import { showError } from "../../helpers";
import Pagination from "../../components/Panigation";

interface PromotionFeedbackTableProps {
  showPromotionColumn?: boolean;
  showUserColumn?: boolean;
}

export default function PromotionFeedbackTable({
  showPromotionColumn = true,
  showUserColumn = true,
}: PromotionFeedbackTableProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || 0) / (pageSize || 10))
  );

  const [rows, setRows] = useState<any[]>([]);
  const [getFeedbacks] = useLazyGetFeedbacksQuery();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  };

  const fetchList = useCallback(async () => {
    try {
      const params: any = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm,
      };
      const response = await getFeedbacks(params).unwrap();
      if (response?.statusCode === 200) {
        const list = response?.data?.data || [];
        const pagination = response?.data?.pagination || {};
        setRows(list);
        setTotalCount(pagination?.total || 0);
      }
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data
        ?.message;
      showError(apiMessage || "Failed to fetch Feedbacks");
    }
  }, [page, pageSize, debouncedSearchTerm, getFeedbacks]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            searchTerm={searchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            onCross={handleClearSearch}
          />
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              {showUserColumn && <TableCell>User Detail</TableCell>}
              {showPromotionColumn && <TableCell>Promotion Name</TableCell>}
              <TableCell>Feedback Provided</TableCell>
              <TableCell>Date & Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).length > 0 ? (
              rows.map((row: any) => (
                <TableRow hover key={row?._id}>
                  {showUserColumn && (
                    <TableCell>
                      <Box
                        component="a"
                        className="user_block click_action"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/manage-users/detail/" + row?.user?._id);
                        }}
                      >
                        <p>
                          <b>{row?.user?.fullName || "-"}</b>
                          <span>{row?.user?.email || ""}</span>
                          <span>
                            {`${row?.user?.dialCode || ""} ${
                              row?.user?.phoneNo || ""
                            }`.trim()}
                          </span>
                        </p>
                      </Box>
                    </TableCell>
                  )}
                  {showPromotionColumn && (
                    <TableCell>
                      <Box component="a" className="user_block click_action">
                        <figure className="no_radius">
                          <img
                            src="/static/images/placeholder.png"
                            alt="Promo"
                          />
                        </figure>
                        <p>
                          <b>{row?.rewardPromotion?.title || "-"}</b>
                        </p>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    {row?.type === "like" ? (
                      <ThumbUpIcon className="c_success" />
                    ) : (
                      <ThumbDownIcon className="c_danger" />
                    )}
                  </TableCell>
                  <TableCell>
                    {row?.createdAt
                      ? new Date(row.createdAt).toLocaleString()
                      : "-"}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    showUserColumn && showPromotionColumn
                      ? 4
                      : showUserColumn || showPromotionColumn
                      ? 3
                      : 2
                  }
                  align="center"
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        module={rows || []}
        page={page}
        setPage={setPage}
        limit={pageSize}
        setLimit={setPageSize}
        onPageChange={onPageChange}
        totalPages={totalPages}
      />
    </>
  );
}
